/**
 * FlowContext.jsx
 * React Context provider for the MCP-based flow context management
 */

import React, {
  createContext, useContext, useState, useEffect,
} from 'react';
import ContextManager from '../models/ContextManager';

// Create Context
const FlowContext = createContext(null);

/**
 * Provider component for flow context
 */
export function FlowContextProvider({ children }) {
  const [contextManager] = useState(() => new ContextManager());
  const [globalVars, setGlobalVars] = useState({});
  const [flowVars, setFlowVars] = useState({});
  const [nodeVars, setNodeVars] = useState({});
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  // Expose all the context functionality
  const value = {
    // Direct access to context values
    globalVars,
    flowVars,
    nodeVars,
    currentNodeId,
    lastUpdate,

    // Set current node (for node-scoped operations)
    setCurrentNode: (nodeId) => {
      setCurrentNodeId(nodeId);
    },

    // Get variable with proper inheritance
    getVar: (key, scope = 'global', nodeId = null) => contextManager.get(key, scope, nodeId || currentNodeId),

    // Set a variable with full MCP metadata
    setVar: (key, value, scope = 'global', nodeId = null, options = {}) => {
      const actualNodeId = nodeId || currentNodeId;
      const success = contextManager.set(
        key,
        value,
        scope,
        actualNodeId,
        options,
      );

      // Update React state if the context changed
      if (success) {
        refreshState(scope, actualNodeId);
      }

      return success;
    },

    // Delete a variable
    deleteVar: (key, scope = 'global', nodeId = null) => {
      const success = contextManager.delete(key, scope, nodeId || currentNodeId);

      if (success) {
        refreshState(scope, nodeId || currentNodeId);
      }

      return success;
    },

    // Check if a variable exists
    hasVar: (key, scope = 'global', nodeId = null, checkParents = true) => contextManager.has(key, scope, nodeId || currentNodeId, checkParents),

    // Get all variables for a scope
    getAllVars: (scope = 'global', nodeId = null) => contextManager.getAll(scope, nodeId || currentNodeId),

    // Get all variables with metadata
    getAllVarsWithMetadata: (scope = 'global', nodeId = null) => {
      const actualNodeId = nodeId || currentNodeId;
      const result = contextManager.getAllWithMetadata(scope, actualNodeId);
      return result;
    },

    // Rollback a variable to a previous version
    rollbackVar: (key, scope = 'global', nodeId = null, version = 0) => {
      const success = contextManager.rollback(key, scope, nodeId || currentNodeId, version);

      if (success) {
        refreshState(scope, nodeId || currentNodeId);
      }

      return success;
    },

    // Export the full context state for saving
    exportContext: () => contextManager.export(),

    // Import a saved context state
    importContext: (data) => {
      contextManager.import(data);
      refreshAllState();
    },
  };

  // Helper function to refresh React state after context changes
  const refreshState = (scope, nodeId) => {
    if (scope === 'global') {
      setGlobalVars(contextManager.getAll('global'));
    } else if (scope === 'flow') {
      setFlowVars(contextManager.getAll('flow'));
    } else if (scope === 'node') {
      // Always refresh all nodeVars
      const allNodeVars = {};
      Object.keys(contextManager.contexts.node || {}).forEach((nid) => {
        allNodeVars[nid] = contextManager.getAll('node', nid);
      });
      setNodeVars(allNodeVars);
    }
    setLastUpdate(Date.now());
  };

  // Helper to refresh all state levels
  const refreshAllState = () => {
    setGlobalVars(contextManager.getAll('global'));
    setFlowVars(contextManager.getAll('flow'));

    // For node vars, we need to handle the multiple node case
    const allNodeVars = {};
    Object.keys(contextManager.contexts.node || {}).forEach((nodeId) => {
      allNodeVars[nodeId] = contextManager.getAll('node', nodeId);
    });
    setNodeVars(allNodeVars);
  };

  return (
    <FlowContext.Provider value={value}>
      {children}
    </FlowContext.Provider>
  );
}

// Custom hook for easier context usage
export function useFlowContext() {
  const context = useContext(FlowContext);
  if (context === null) {
    throw new Error('useFlowContext must be used within a FlowContextProvider');
  }
  return context;
}

export default FlowContext;
